// src/encounter/lib/lab-print-extras.ts
// PROD-2849, wrap-up's order view (the Post-Visit Summary opened by "Preview orders"): what the lab
// orders add to it on the agreed lab model.
//   · the patient prep of each lab order ("Fasting 8 h preferred"), from the Add lab popup's order;
//   · the tests that go as Patient's choice of lab, listed for the patient to take to any lab: from this
//     session's orders, and from Scrips.Patient's lab requisitions once the encounter is signed
//     (PROD-2841: `GET api/Patients/{patientId}/Encounters/{encounterId}/LabRequisitions`).
// The prep is not stored by Patient, so an order reloaded from the server shows none.
import type { PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { OrderPayloadMap } from './order-payload';

export interface LabPrintPrep {
  /** The order's code (matched against the summary's lab code). */
  code?: string;
  /** The order's name (matched against the summary's lab name). */
  name: string;
  prep: string;
}

export interface LabPrintExtras {
  preps: LabPrintPrep[];
  /** Test names, in order, without repeats. */
  patientsChoiceTests: string[];
}

/** One requisition as Patient returns it (only the fields this needs). */
export interface LabRequisitionLite {
  fulfilmentMode?: string | null;
  status?: string | null;
  tests?: { display?: string | null; status?: string | null }[] | null;
}

export function toLabPrintExtras(
  orders: readonly PlanOrder[],
  payloads: OrderPayloadMap,
  requisitions?: readonly LabRequisitionLite[] | null,
): LabPrintExtras {
  const preps: LabPrintPrep[] = [];
  const pc: string[] = [];
  const add = (name?: string | null) => {
    const n = name?.trim();
    if (n && !pc.some((x) => x.toLowerCase() === n.toLowerCase())) pc.push(n);
  };
  for (const o of orders) {
    if (o.type !== 'lab') continue;
    const p = payloads.get(o.id);
    if (p?.kind !== 'lab' || !p.orderSearch) continue;
    const name = p.code.displayName || o.label;
    if (p.orderSearch.prep) preps.push({ code: p.code.code, name, prep: p.orderSearch.prep });
    if (p.orderSearch.patientsChoice) add(name);
    p.orderSearch.patientsChoiceTests?.forEach(add);
  }
  for (const r of requisitions ?? []) {
    if ((r.fulfilmentMode ?? '').toLowerCase() !== 'patientchoice' || (r.status ?? '').toLowerCase() === 'cancelled') continue;
    for (const t of r.tests ?? []) if ((t.status ?? '').toLowerCase() !== 'cancelled') add(t.display);
  }
  return { preps, patientsChoiceTests: pc };
}

const same = (a?: string | null, b?: string | null) =>
  !!a?.trim() && !!b?.trim() && a.trim().toLowerCase() === b.trim().toLowerCase();

/** The prep line for one summary lab row (matched on code, else name). */
export function prepForLab(preps: readonly LabPrintPrep[] | undefined, labCode?: string | null, labName?: string | null): string | null {
  const hit = (preps ?? []).find((p) => same(p.code, labCode)) ?? (preps ?? []).find((p) => same(p.name, labName));
  return hit?.prep ?? null;
}
